<template>
  <!-- v-if drops the modal subtree on close, which unmounts the
       ChargebeePaymentForm and lets its onBeforeUnmount call into the
       composable's unmount handle — that closes both chargebee
       components and calls Chargebee.tearDown(), so the next open
       starts from a clean singleton (chargebee Payment Components only
       supports one live `payment` / `payment-button` instance per
       page). -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-onyx/60 px-2 sm:px-4"
    role="dialog"
    aria-modal="true"
  >
    <!-- Width: max-w-xl (576px) gives the chargebee payment-method tabs +
         card inputs enough horizontal room. Below sm the backdrop/dialog
         padding shrinks (px-2 / p-4): the chargebee iframe sizes itself
         from this container and on a ~390px viewport the wider padding
         clipped the card brand icons (TV-1746). Height: cap at 90vh and
         let the dialog scroll internally if the chargebee form grows past
         the viewport (multi-tab forms with billing address can get tall). -->
    <div
      class="relative flex flex-col bg-depth-2 rounded-lg shadow-xl max-w-xl w-full p-4 sm:p-6 outline outline-depth-3 max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-bold text-mouse">
          {{ $t('support/payment_method/title') }}
        </h3>
        <button
          type="button"
          class="text-rhino hover:text-mouse text-xl leading-none"
          :aria-label="t('frontend/close')"
          @click="closeModal"
        >
          ×
        </button>
      </div>

      <div v-if="error" class="space-y-4">
        <div class="p-3 bg-danger/10 text-danger-fg rounded-lg">
          {{ error }}
        </div>
        <button
          type="button"
          class="uppercase tracking-wider w-full py-2 px-3 bg-depth-3 text-mouse rounded-lg font-semibold"
          @click="closeModal"
        >
          {{ $t('general/confirm/cancel_btn_text') }}
        </button>
      </div>

      <!-- Brief success confirmation. Shown the instant the setup RPC
           returns, replacing the now-torn-down chargebee form so the modal
           never lingers as an empty panel (QA finding 1.2). Auto-closes
           after a short beat. -->
      <div v-else-if="saved" class="flex flex-col items-center gap-4 py-6">
        <div
          class="flex size-12 items-center justify-center rounded-full bg-success/10"
        >
          <svg
            class="size-6 text-success"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
        <p class="text-mouse font-semibold">
          {{ $t('support/payment_method/success') }}
        </p>
      </div>

      <div v-else class="space-y-4">
        <checkout-payment-notes
          :notes="[$t('payment/form/new_method_primary_notice')]"
        />

        <div class="relative min-h-[16rem]">
          <div
            v-if="loading || !formReady"
            class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-lg bg-depth-2"
          >
            <div
              class="size-10 rounded-full border-4 border-depth-3 border-t-brand animate-spin"
            ></div>
            <p class="text-rhino text-sm">
              {{ $t('support/payment_method/title') }}
            </p>
          </div>

          <chargebee-payment-form
            v-if="paymentIntent"
            :payment-intent="paymentIntent"
            :payment-methods="SETUP_PAYMENT_METHODS"
            :on-submit="submitSetup"
            :submit-button-label="$t('support/payment_method/title')"
            :surface-color="GP_DEPTH_2"
            suppress-mount-loader
            @ready="formReady = true"
            @success="onSuccess"
            @error="onComponentError"
            @close="closeModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CreateSetupPaymentIntentRequest,
  SubmitPaymentMethodSetupRequest,
} from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import type { CreateSetupPaymentIntentResponse } from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import { captureException } from '@sentry/nuxt';
import ChargebeePaymentForm from '~/components/payment/ChargebeePaymentForm.vue';
import {
  chargebeePaymentIntentFromProto,
  GP_DEPTH_2,
  SETUP_PAYMENT_METHODS,
} from '~/composables/useChargebeePayment';
import useGrpcPayment from '~/composables/useGrpcPayment';

const { t } = useI18n();

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const loading = ref(false);
const error = ref('');
const saved = ref(false);
const formReady = ref(false);
const paymentIntent = ref<Record<string, unknown> | null>(null);

// Holds the auto-close timer for the post-success confirmation so we can
// cancel it if the user closes manually first (or the modal unmounts).
let successTimer: ReturnType<typeof setTimeout> | null = null;
const SUCCESS_DISPLAY_MS = 1_500;
// Bumped on every trigger()/closeModal() so a fetchSetupIntent() that resolves
// after the modal closed (or was re-triggered) can't assign a stale intent.
let setupRequestId = 0;

function closeModal() {
  if (successTimer) {
    clearTimeout(successTimer);
    successTimer = null;
  }
  // Invalidate any in-flight setup-intent fetch so it can't reassign
  // paymentIntent after we clear it below.
  setupRequestId++;
  // Clear paymentIntent so the v-else-if drops ChargebeePaymentForm;
  // its onBeforeUnmount fires the composable's teardown (close() on
  // both chargebee components + Chargebee.tearDown()) and the next
  // open re-fetches a fresh intent and re-creates the singleton.
  paymentIntent.value = null;
  loading.value = false;
  error.value = '';
  saved.value = false;
  formReady.value = false;
  emit('close');
}

const buildAuthMetadata = useAuthMetadata();

async function fetchSetupIntent(): Promise<Record<string, unknown>> {
  const $grpcPayment = useGrpcPayment();
  const { response, error: grpcError } =
    await $grpcPayment.executeApiCallActive(
      'createSetupPaymentIntent',
      new CreateSetupPaymentIntentRequest({}),
      buildAuthMetadata(),
    );

  if (grpcError != null) {
    captureException(grpcError, {
      extra: {
        context: 'PaymentMethodModal.createSetupPaymentIntent',
        code: grpcError.code,
        details: grpcError.details,
      },
    });
    throw new Error(t('support/payment_method/generic_error'));
  }

  const data = response as unknown as CreateSetupPaymentIntentResponse;
  if (!data?.paymentIntent?.id) {
    captureException(
      new Error(
        '[PaymentMethodModal] CreateSetupPaymentIntent response missing payment_intent',
      ),
    );
    throw new Error(t('support/payment_method/generic_error'));
  }

  return chargebeePaymentIntentFromProto(data.paymentIntent);
}

async function submitSetup(paymentIntentId: string): Promise<void> {
  const $grpcPayment = useGrpcPayment();
  const { error: grpcError } = await $grpcPayment.executeApiCallActive(
    'submitPaymentMethodSetup',
    new SubmitPaymentMethodSetupRequest({ paymentIntentId }),
    buildAuthMetadata(),
  );
  if (grpcError != null) {
    captureException(grpcError, {
      extra: {
        context: 'PaymentMethodModal.submitPaymentMethodSetup',
        code: grpcError.code,
        details: grpcError.details,
      },
    });
    throw new Error(t('support/payment_method/generic_error'));
  }
}

function onSuccess() {
  // Let the parent refresh its SPM state in the background.
  emit('success');
  // Drop the chargebee form immediately (fires its teardown) and show a
  // short confirmation instead of leaving the emptied form on screen,
  // then auto-close. closeModal clears successTimer if the user closes
  // sooner.
  paymentIntent.value = null;
  loading.value = false;
  error.value = '';
  saved.value = true;
  if (successTimer) clearTimeout(successTimer);
  successTimer = setTimeout(closeModal, SUCCESS_DISPLAY_MS);
}

function onComponentError(err: unknown) {
  captureException(err, {
    extra: { context: 'PaymentMethodModal.ChargebeePaymentForm' },
  });
  error.value = t('support/payment_method/generic_error');
  loading.value = false;
}

async function trigger() {
  loading.value = true;
  error.value = '';
  saved.value = false;
  formReady.value = false;
  const requestId = ++setupRequestId;
  try {
    const intent = await fetchSetupIntent();
    // Drop the result if the modal was closed or re-triggered while in flight.
    if (requestId !== setupRequestId) return;
    paymentIntent.value = intent;
  } catch (e) {
    if (requestId !== setupRequestId) return;
    captureException(e, {
      extra: { context: 'PaymentMethodModal.fetchSetupIntent' },
    });
    error.value = t('support/payment_method/generic_error');
  } finally {
    if (requestId === setupRequestId) loading.value = false;
  }
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) trigger();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (successTimer) clearTimeout(successTimer);
});
</script>
